import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLocation } from "wouter";
import { useAuth0 } from '@auth0/auth0-react';
import { clearAuthArtifacts, clearAppCookies } from '@/auth/auth-recovery';

export default function LoginScreen() {
  const [, setLocation] = useLocation();
  const { isAuthenticated, loginWithRedirect, getIdTokenClaims, isLoading: isAuth0Loading } = useAuth0();
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string;
  const defaultConnection = import.meta.env.VITE_AUTH0_DEFAULT_CONNECTION as string | undefined;
  const isMockAuth = (import.meta.env.VITE_MOCK_AUTH as any) === true || (import.meta.env.VITE_MOCK_AUTH as any) === 'true';
  const debugFromUrl = (() => {
    try { return new URLSearchParams(window.location.search).get('debug') === '1'; } catch { return false; }
  })();
  const debugFromStorage = (() => {
    try {
      const v = localStorage.getItem('auth0_debug');
      return v === '1' || (v || '').toLowerCase() === 'true';
    } catch { return false; }
  })();
  const exchangeDebug = debugFromUrl || debugFromStorage;
  const hasAuthParams = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.has('code') || sp.has('state');
    } catch { return false; }
  })();

  // Dev mock: if enabled and no token, request a server-issued token based on dev session
  useEffect(() => {
    if (!isMockAuth) return;
    const bootstrapDev = async () => {
      if (!localStorage.getItem('jwt_token')) {
        try {
          const res = await fetch('/api/auth/token', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
          if (res.ok) {
            const data = await res.json();
            if (data?.token) {
              localStorage.setItem('jwt_token', data.token);
              try { window.dispatchEvent(new Event('auth:token')); } catch {}
              // Redirect to stored or default route
              const redirect = sessionStorage.getItem('postLoginRedirect');
              try { sessionStorage.removeItem('postLoginRedirect'); } catch {}
              const roleNorm = (data.user?.role || '').toString().toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
              if (redirect && redirect !== '/login') {
                window.location.assign(redirect);
              } else if (roleNorm === 'superadmin' || roleNorm === 'super_admin') {
                window.location.assign('/admin');
              } else {
                window.location.assign('/');
              }
              return;
            }
          }
        } catch (e) {
          // fall back to Auth0 flow below
        }
      }
    };
    bootstrapDev();
  }, [isMockAuth]);

  // Safety net: if we are stuck here for a few seconds, clear stale cache and retry login
  useEffect(() => {
    if (isMockAuth) return; // skip Auth0 redirect when mocking
    if (isAuthenticated || hasAuthParams) return;
    const t = window.setTimeout(() => {
      if (!isAuthenticated && !isAuth0Loading) {
        clearAuthArtifacts(clientId);
        clearAppCookies();
        loginWithRedirect();
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [isAuthenticated, isAuth0Loading, hasAuthParams, loginWithRedirect, clientId, isMockAuth]);

  // On first mount, if not authenticated, redirect to Auth0
  useEffect(() => {
    if (isMockAuth) return; // skip when mocking
    if (!isAuth0Loading && !isAuthenticated && !hasAuthParams) {
      // Only initiate redirect if we're not already on the callback with code/state
      if (defaultConnection) {
        loginWithRedirect({
          authorizationParams: { connection: defaultConnection }
        });
      } else {
        loginWithRedirect();
      }
    }
  }, [isAuth0Loading, isAuthenticated, hasAuthParams, loginWithRedirect, defaultConnection, isMockAuth]);

  // If returned from Auth0, exchange id_token for internal JWT
  useEffect(() => {
    if (isMockAuth) return; // not needed when mocking
    const exchangeIfNeeded = async () => {
      if (isAuthenticated && !localStorage.getItem('jwt_token')) {
        try {
          const claims = await getIdTokenClaims();
          const idToken = (claims as any)?.__raw;
          if (!idToken) return;
          const debugQs = exchangeDebug ? '?debug=1' : '';
          const res = await fetch(`/api/auth/auth0/exchange${debugQs}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id_token: idToken })
          });
          if (res.ok) {
            const result = await res.json();
            localStorage.setItem('jwt_token', result.token);
            // Notify listeners (e.g., formula engine) that an auth token is now available
            try { window.dispatchEvent(new Event('auth:token')); } catch {}
            // redirect similar to password login
            const redirect = sessionStorage.getItem('postLoginRedirect');
            const go = (path: string) => {
              try { sessionStorage.removeItem('postLoginRedirect'); } catch {}
              window.location.assign(path);
            };
            const roleNorm = (result.user?.role || '').toString().toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
            if (redirect && redirect !== '/login') {
              go(redirect);
            } else if (roleNorm === 'superadmin' || roleNorm === 'super_admin') {
              go('/admin');
            } else {
              go('/');
            }
          } else {
            // If exchange fails (401, 403, etc.), clear artifacts and retry login cleanly
            clearAuthArtifacts(clientId);
            clearAppCookies();
            await loginWithRedirect();
            try {
              const err = await res.json();
              if (exchangeDebug && err?.debug) {
                // eslint-disable-next-line no-console
                console.warn('[auth0:exchange] server debug:', err.debug);
              }
              if (exchangeDebug) {
                // eslint-disable-next-line no-console
                console.warn('[auth0:exchange] http', res.status, err);
              }
            } catch {
              if (exchangeDebug) {
                // eslint-disable-next-line no-console
                console.warn('[auth0:exchange] http', res.status);
              }
            }
          }
        } catch (e: any) {
          const code = e?.error || e?.code || '';
          const recoverable = ['login_required','consent_required','invalid_grant','mfa_required'];
          if (recoverable.some(x => code.includes(x))) {
            clearAuthArtifacts(clientId);
            clearAppCookies();
            await loginWithRedirect();
            return;
          }
          console.warn('Auth0 exchange failed:', e);
        }
      }
    };
    exchangeIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isMockAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{isMockAuth ? 'Signing in (dev)…' : 'Redirecting to Login…'}</CardTitle>
          <CardDescription>
            {isMockAuth ? 'Using local dev session.' : 'Connecting to secure Auth0 sign-in.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-center text-muted-foreground">
            {isMockAuth ? 'Please wait…' : (isAuth0Loading ? 'Preparing authentication…' : 'Redirecting…')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
